import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaSave, FaArrowLeft, FaUser, FaImage } from 'react-icons/fa';
import { createAdmin, clearState } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';

import { Link, useNavigate } from 'react-router-dom';

const AddAdmin = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, success } = useSelector((state) => state.auth);

  const [adminData, setAdminData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);

  useEffect(() => {
    setTitle('إضافة مشرف جديد');
    dispatch(clearState()); // تنظيف الـ success state القديم
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearState());
      navigate('/admins'); // العودة لصفحة المشرفين
    }
    if (error) {
      showErrorToast(error);
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'image') {
      handleImageChange(files?.[0]);
      return;
    }

    setAdminData(prev => ({ 
      ...prev, 
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // تنظيف أي رسائل قديمة قبل الإرسال
    dispatch(clearState());

    if (imageProcessing) {
      showWarningToast('يرجى الانتظار حتى يتم تجهيز الصورة قبل الإرسال');
      return;
    }
    
    if (!adminData.name?.trim()) {
      showErrorToast('يرجى إدخال اسم المشرف');
      return;
    }
    
    if (!adminData.email?.trim()) {
      showErrorToast('يرجى إدخال البريد الإلكتروني');
      return;
    }
    
    if (!adminData.password?.trim()) {
      showErrorToast('يرجى إدخال كلمة المرور');
      return;
    }

    if (!adminData.image) {
      showErrorToast('يرجى اختيار صورة للمشرف');
      return;
    }

    // إنشاء FormData لإرسال البيانات مع الصورة
    const formData = new FormData();
    formData.append('name', adminData.name);
    formData.append('email', adminData.email);
    formData.append('password', adminData.password);
    if (adminData.phone) formData.append('phone', adminData.phone);
    if (adminData.image) formData.append('image', adminData.image);

    dispatch(createAdmin(formData));
  };

  const handleImageChange = async (file) => {
    if (!file) {
      setAdminData(prev => ({ ...prev, image: null }));
      setImagePreview(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      showErrorToast('يرجى اختيار صورة صالحة');
      return;
    }

    setImageProcessing(true);

    try {
      const [compressedResult] = await compressFiles([file], {
        maxSize: 800000,
        quality: 0.85,
      });

      const finalFile = compressedResult instanceof File
        ? compressedResult
        : new File([compressedResult], file.name, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now(),
          });

      setAdminData(prev => ({ ...prev, image: finalFile }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageProcessing(false);
      };
      reader.onerror = () => {
        setImagePreview(null);
        setImageProcessing(false);
        showErrorToast('حدث خطأ أثناء قراءة الصورة');
      };
      reader.readAsDataURL(finalFile);
    } catch (error) {
      setImageProcessing(false);
      showErrorToast('فشل في معالجة الصورة، يرجى المحاولة مرة أخرى');
    }
  };

  const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      
      
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/admins" className="text-decoration-none mx-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: '8px' }}
              >
                <FaArrowLeft className="mx-2" />
                العودة
              </motion.button>
            </Link>
            <h4 className="mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.1rem' }}>
              <FaUser className="mx-2" />
              إضافة مشرف جديد
            </h4>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '15px' }}
          >
            <div className="card-header bg-white border-0" style={{ padding: '2rem' }}>
              <h4 className="text-center mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.3rem' }}>
                بيانات المشرف الجديد
              </h4>
            </div>

            <div className="card-body" style={{ padding: '10px 30px' }}>
              <form onSubmit={handleSubmit}>
                {/* Name and Email */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">اسم المشرف</label>
                    <input
                      type="text"
                      name="name"
                      value={adminData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل اسم المشرف"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">البريد الإلكتروني</label>
                    <input
                      type="email"
                      name="email"
                      value={adminData.email}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل البريد الإلكتروني"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                </div>

                {/* Password and Phone */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">كلمة المرور</label>
                    <input
                      type="password"
                      name="password"
                      value={adminData.password}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل كلمة المرور"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">رقم الهاتف</label>
                    <input
                      type="tel"
                      name="phone"
                      value={adminData.phone}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل رقم الهاتف"
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div className="mb-4">
                  <label className="form-label fw-medium">صورة المشرف</label>
                  <input
                    type="file"
                    name="image"
                    onChange={handleInputChange}
                    className="form-control"
                    accept="image/*"
                    disabled={imageProcessing}
                    style={{ borderRadius: '10px' }}
                  />
                  {imageProcessing && (
                    <div className="alert alert-info py-2 px-3 mt-2">
                      جاري تجهيز الصورة، يرجى الانتظار...
                    </div>
                  )}
                  {imagePreview && !imageProcessing && (
                    <div className="mt-3 text-center">
                      <img
                        src={imagePreview}
                        alt="معاينة صورة المشرف"
                        style={{
                          maxWidth: '180px',
                          maxHeight: '180px',
                          borderRadius: '12px',
                          objectFit: 'cover',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="btn btn-primary px-5 py-3"
                    disabled={loading || imageProcessing || !adminData.image}
                    style={{ 
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      backgroundColor: '#184194',
                      border: 'none'
                    }}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm mx-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        جاري الحفظ...
                      </>
                    ) : (
                      <>
                        <FaSave className="mx-2" />
                        حفظ المشرف
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AddAdmin;
